import axios, { AxiosError } from "axios";
import { FirebaseError } from "firebase/app";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { auth } from "../../auth/firebase";
import { httpClient } from "../http/client";
import { syncTryNowJourneyToProfile } from "../users/userService";
import { refreshSubscriptionState } from "../subscription/subscriptionService";
import { resetSubscriptionState } from "../subscription/subscriptionStore";
import {
  clearAuthState,
  clearExplicitSignOutRecord,
  getAuthState,
  hasExplicitSignOutMark,
  recordExplicitUserSignOut,
  setNeedsOnboarding,
  setSignedIn,
} from "./authStore";
import type { Applicant, AuthState, SignupPayload } from "./types";

const STORAGE_APPLICANT_CACHE = "hallticket.applicant.v1";

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getApplicantFromStorage(): Applicant | null {
  if (!canUseLocalStorage()) {
    return null;
  }
  const raw = window.localStorage.getItem(STORAGE_APPLICANT_CACHE);
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return parseApplicantFromApi(parsed);
  } catch {
    return null;
  }
}

function setApplicantInStorage(applicant: Applicant): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.setItem(STORAGE_APPLICANT_CACHE, JSON.stringify(applicant));
}

function clearApplicantFromStorage(): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.removeItem(STORAGE_APPLICANT_CACHE);
}

interface LoginApiResponse {
  applicantExists?: boolean;
  exists?: boolean;
  isSignedUp?: boolean;
  isExists?: boolean;
  notExists?: boolean;
  isNotExists?: boolean;
  needsOnboarding?: boolean;
  data?: unknown;
  result?: unknown;
  applicant?: Applicant;
  user?: Applicant;
}

interface SignupApiResponse {
  success?: boolean;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseApplicant(value: unknown): Applicant | null {
  if (!isObject(value)) {
    return null;
  }

  const id = value.id;
  const firstName = value.firstName;
  const lastName = value.lastName;
  const email = value.email;
  const mobileNo = value.mobileNo;
  const dateOfBirth = value.dateOfBirth;
  const status = value.status;
  const gender = value.gender;
  const nationality = value.nationality;
  const photoUrl = value.photoUrl;
  const createdAt = value.createdAt;
  const updatedAt = value.updatedAt;
  const category = value.category;
  const pinCode = value.pinCode ?? value.pin_code;
  const address = value.address;
  const city = value.city;
  const stateOfResidence = value.stateOfResidence ?? value.state_of_residence;
  const stateOfDomicile = value.stateOfDomicile ?? value.state_of_domicile;
  const verticalReservation = value.verticalReservation ?? value.vertical_reservation;
  const horizontalReservations = value.horizontalReservations ?? value.horizontal_reservations;

  if (typeof id !== "number" || typeof status !== "string" || typeof createdAt !== "string" || typeof updatedAt !== "string") {
    return null;
  }

  if (
    (firstName !== null && typeof firstName !== "string") ||
    (lastName !== null && typeof lastName !== "string") ||
    (email !== null && typeof email !== "string") ||
    (mobileNo !== null && typeof mobileNo !== "string") ||
    (dateOfBirth !== null && typeof dateOfBirth !== "string") ||
    (gender !== null && typeof gender !== "string") ||
    (nationality !== null && typeof nationality !== "string") ||
    (photoUrl !== null && typeof photoUrl !== "string") ||
    (category !== undefined && category !== null && typeof category !== "string") ||
    (verticalReservation !== undefined && verticalReservation !== null && typeof verticalReservation !== "string") ||
    (horizontalReservations !== undefined
      && horizontalReservations !== null
      && (!Array.isArray(horizontalReservations) || horizontalReservations.some((item) => typeof item !== "string"))) ||
    (pinCode !== undefined && pinCode !== null && typeof pinCode !== "string") ||
    (address !== undefined && address !== null && typeof address !== "string") ||
    (city !== undefined && city !== null && typeof city !== "string") ||
    (stateOfResidence !== undefined && stateOfResidence !== null && typeof stateOfResidence !== "string") ||
    (stateOfDomicile !== undefined && stateOfDomicile !== null && typeof stateOfDomicile !== "string")
  ) {
    return null;
  }

  const applicant: Applicant = {
    id,
    firstName: typeof firstName === "string" ? firstName : "",
    lastName: typeof lastName === "string" ? lastName : "",
    email: typeof email === "string" ? email : "",
    mobileNo,
    dateOfBirth,
    status,
    gender,
    nationality,
    photoUrl,
    createdAt,
    updatedAt,
  };

  if (typeof category === "string" || category === null) {
    applicant.category = category;
  }
  if (typeof verticalReservation === "string" || verticalReservation === null) {
    applicant.verticalReservation = verticalReservation;
  }
  if (Array.isArray(horizontalReservations)) {
    applicant.horizontalReservations = horizontalReservations
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (typeof pinCode === "string" || pinCode === null) {
    applicant.pinCode = pinCode;
  }
  if (typeof address === "string" || address === null) {
    applicant.address = address;
  }
  if (typeof city === "string" || city === null) {
    applicant.city = city;
  }
  if (typeof stateOfResidence === "string" || stateOfResidence === null) {
    applicant.stateOfResidence = stateOfResidence;
  }
  if (typeof stateOfDomicile === "string" || stateOfDomicile === null) {
    applicant.stateOfDomicile = stateOfDomicile;
  }

  return applicant;
}

/** Parse applicant JSON from login/update API responses. */
export function parseApplicantFromApi(value: unknown): Applicant | null {
  return parseApplicant(value);
}

function getWrappedValue(data: LoginApiResponse): unknown {
  if (data.applicant !== undefined) {
    return data.applicant;
  }
  if (data.user !== undefined) {
    return data.user;
  }
  if (data.data !== undefined) {
    return data.data;
  }
  if (data.result !== undefined) {
    return data.result;
  }

  return data;
}

function parseApplicantFromLoginPayload(data: LoginApiResponse): Applicant | null {
  const wrapped = getWrappedValue(data);
  const directApplicant = parseApplicant(wrapped);
  if (directApplicant) {
    return directApplicant;
  }

  if (isObject(wrapped)) {
    return parseApplicant(wrapped.applicant) ?? parseApplicant(wrapped.user);
  }

  return null;
}

function parseLoginResponse(data: unknown): { exists: boolean; applicant: Applicant | null } {
  if (!isObject(data)) {
    return { exists: false, applicant: null };
  }

  const typed = data as LoginApiResponse;
  const applicant = parseApplicantFromLoginPayload(typed);
  const wrapped = getWrappedValue(typed);
  const wrappedObject = isObject(wrapped) ? wrapped : null;
  const notExistsFlag = typed.notExists ?? typed.isNotExists ?? typed.needsOnboarding ?? wrappedObject?.notExists ?? wrappedObject?.isNotExists ?? wrappedObject?.needsOnboarding ?? false;

  if (Boolean(notExistsFlag)) {
    return { exists: false, applicant: null };
  }

  const existsFlag =
    typed.applicantExists ??
    typed.exists ??
    typed.isExists ??
    typed.isSignedUp ??
    wrappedObject?.applicantExists ??
    wrappedObject?.exists ??
    wrappedObject?.isExists ??
    wrappedObject?.isSignedUp ??
    (typeof wrappedObject?.id === "number" ? true : undefined) ??
    applicant !== null;

  return {
    exists: Boolean(existsFlag),
    applicant,
  };
}

function createFallbackApplicant(): Applicant {
  return {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    mobileNo: null,
    dateOfBirth: null,
    status: "",
    gender: null,
    nationality: null,
    photoUrl: null,
    createdAt: "",
    updatedAt: "",
  };
}

const FIREBASE_AUTH_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-verification-code": "The verification code is incorrect. Please try again.",
  "auth/code-expired": "The verification code has expired. Please request a new code.",
  "auth/session-expired": "Your verification session has expired. Please request a new code.",
  "auth/invalid-phone-number": "Please enter a valid mobile number.",
  "auth/missing-phone-number": "Please enter your mobile number.",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/quota-exceeded": "OTP service is temporarily unavailable. Please try again later.",
  "auth/network-request-failed": "Network issue detected. Please check your internet connection and try again.",
  "auth/captcha-check-failed": "Verification failed. Please try again.",
  "auth/invalid-app-credential": "Verification failed. Please request a new code and try again.",
};

function getFirebaseAuthMessage(error: unknown): string | null {
  if (error instanceof FirebaseError) {
    return FIREBASE_AUTH_ERROR_MESSAGES[error.code] ?? "Verification failed. Please try again.";
  }

  if (isObject(error) && typeof error.code === "string" && error.code.startsWith("auth/")) {
    return FIREBASE_AUTH_ERROR_MESSAGES[error.code] ?? "Verification failed. Please try again.";
  }

  if (error instanceof Error) {
    const firebaseAuthCodeMatch = error.message.match(/auth\/[a-z-]+/i);
    if (firebaseAuthCodeMatch) {
      const code = firebaseAuthCodeMatch[0].toLowerCase();
      return FIREBASE_AUTH_ERROR_MESSAGES[code] ?? "Verification failed. Please try again.";
    }
  }

  return null;
}

export async function loginApplicant(tokenOverride?: string): Promise<{ exists: boolean; applicant: Applicant | null }> {
  const response = await httpClient.get<unknown>("/api/v1/auth/login", {
    headers: tokenOverride
      ? {
          Authorization: `Bearer ${tokenOverride}`,
        }
      : undefined,
  });

  return parseLoginResponse(response.data);
}

export async function resolvePostFirebaseLogin(firebaseIdToken: string): Promise<AuthState> {
  let login: { exists: boolean; applicant: Applicant | null };

  try {
    login = await loginApplicant(firebaseIdToken);
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 401)) {
      setNeedsOnboarding(firebaseIdToken);
      return getAuthState();
    }
    throw error;
  }

  if (login.exists) {
    const nextApplicant = login.applicant ?? createFallbackApplicant();
    setSignedIn(firebaseIdToken, nextApplicant);
    setApplicantInStorage(nextApplicant);
    await syncTryNowJourneyToProfile(getAuthState().applicant ?? nextApplicant);
    void refreshSubscriptionState().catch(() => {
      // Keep session usable even if subscription read fails.
    });
    return getAuthState();
  }

  setNeedsOnboarding(firebaseIdToken);
  return getAuthState();
}

export async function bootstrapAuthSession(): Promise<AuthState> {
  // Session is per-tab; Firebase auth can still be restored from IndexedDB after a local sign-out.
  // If the user explicitly signed out in this browser, drop any stale Firebase user before restoring.
  if (hasExplicitSignOutMark()) {
    if (auth.currentUser) {
      try {
        await signOut(auth);
      } catch {
        // Best effort; if Firebase still has a user, keep the mark so the next load retries.
      }
    }
    if (!auth.currentUser) {
      clearExplicitSignOutRecord();
    } else {
      // User explicitly signed out in app; do not re-hydrate `needs_onboarding` from a stale Firebase user.
      clearAuthState();
      clearApplicantFromStorage();
      return getAuthState();
    }
  }

  const firebaseUser = await new Promise<User | null>((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

  if (!firebaseUser) {
    clearAuthState();
    clearApplicantFromStorage();
    return getAuthState();
  }

  const idToken = await firebaseUser.getIdToken(true);
  return resolvePostFirebaseLogin(idToken);
}

export async function signOutUser(): Promise<void> {
  try {
    await signOut(auth);
  } finally {
    clearApplicantFromStorage();
    resetSubscriptionState();
    clearAuthState();
    recordExplicitUserSignOut();
  }
}

export function validateSignupPayload(payload: SignupPayload): string[] {
  const errors: string[] = [];
  const mobile = payload.mobile.trim();
  const mobileNo = (payload.mobileNo ?? "").trim();
  const hasMobile = /^\+91\d{10}$/.test(mobile);
  const hasMobileNo = /^\d{10}$/.test(mobileNo);

  if (!mobile && !mobileNo) {
    errors.push("Please enter your mobile number.");
  } else if (!hasMobile && !hasMobileNo) {
    errors.push("Please enter a valid mobile number.");
  }

  return errors;
}

export async function signupApplicant(payload: SignupPayload, tokenOverride?: string): Promise<AuthState> {
  const errors = validateSignupPayload(payload);
  if (errors.length > 0) {
    throw new Error(errors[0]);
  }

  const currentState = getAuthState();
  const firebaseIdToken = tokenOverride ?? currentState.firebaseIdToken;
  if (!firebaseIdToken) {
    clearAuthState();
    throw new Error("Your session has expired. Please sign in again.");
  }

  await httpClient.post<SignupApiResponse>("/api/v1/auth/signup", payload, {
    headers: {
      Authorization: `Bearer ${firebaseIdToken}`,
    },
  });

  const login = await loginApplicant(firebaseIdToken);
  if (!login.exists || !login.applicant) {
    throw new Error("Signup completed but profile could not be loaded. Please try again.");
  }

  setSignedIn(firebaseIdToken, login.applicant);
  setApplicantInStorage(login.applicant);
  await syncTryNowJourneyToProfile(login.applicant);
  return getAuthState();
}

export function getAuthErrorMessage(error: unknown): string {
  const firebaseAuthMessage = getFirebaseAuthMessage(error);
  if (firebaseAuthMessage) {
    return firebaseAuthMessage;
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<unknown>;
    const data = axiosError.response?.data;

    if (typeof data === "string" && data.trim()) {
      return data;
    }

    if (isObject(data)) {
      const message = data.message;
      const detail = data.detail;
      if (typeof message === "string" && message.trim()) {
        return message;
      }
      if (typeof detail === "string" && detail.trim()) {
        return detail;
      }
    }

    return "Request failed. Please try again.";
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}
